/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */

import { ICustomerDTO } from "@hooks/dtos/user-dto";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ZuLoadingStore } from "@hooks/stores/zustand-loading-store";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { config } from "config";
import { ReactNode, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface ISignInProps {
  userName: string;
  password: string;
}

interface IRegisterProps {
  gender?: number;
  userName: string;
  name: string;
  password: string;
  passwordConfirm: string;
  email: string;
  emailConfirm: string;
  cellPhone: string;
  termsCheck: string;
  birth?: string;
  language: string;
  document?: string;
  atSign: string;
  referral?: number;
}

interface ICustomerExistsProps {
  email?: string;
  userName?: string;
  document?: string;
  atSign?: string;
}

interface AuthContextData {
  signIn(data: ISignInProps): Promise<void>;
  createCustomer(data: IRegisterProps): Promise<void>;
  checkCustomerExists(data: ICustomerExistsProps): Promise<Record<string, boolean> | null>;
  signOut: () => void;
  getUserProfile: () => Promise<void>;
  getCustomerBalance: () => Promise<void>;
  customer: ICustomerDTO | undefined;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const {
    customer,
    balance,
    actions: { setCustomer, setBalance, setSessionId },
  } = ZuAuthStore();

  const {
    actions: { toggleLockLoading },
  } = ZuLoadingStore();

  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  function clearHash() {
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  async function openSession() {
    toggleLockLoading(true);
    try {
      await api.get("/customer/session/open");
    } catch (error) {
      // console.log('error auth: ', error)
    }
    toggleLockLoading(false);
  }

  async function getSessionInfo() {
    try {
      const response = await api.get("/customer/session/info");

      return response;
    } catch (err) {
      //
    }
  }

  async function signIn(data: ISignInProps) {
    await openSession();

    const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
    try {
      const response = await api.post("/customer/session/sign-in", {
        userName: data.userName,
        password: data.password,
        platform: "s",
        token: sessionId,
        csrfInfo: sessionId,
      });

      const { customer, customerInfo } = response.data;

      setCustomer({ customer, customerInfo });

      pushToastify({
        message: "Login realizado com sucesso!",
        type: "success",
        theme: "dark",
      });

      clearHash();

      getCustomerBalance();

      navigate("/dashboard");
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  }

  async function createCustomer(data: IRegisterProps) {
    try {
      const response = await api.post("/customer/create", {
        customer: {
          gender: data.gender,
          userName: data.userName,
          name: data.name,
          password: data.password,
          passwordConfirm: data.passwordConfirm,
          email: data.email,
          emailConfirm: data.email,
          cellPhone: data.cellPhone,
          termsCheck: data.termsCheck ? "y" : "n",
          birth: data.birth,
          language: "pt-br",
          referral: data.referral,
          document: data.document,
          documentType: 1,
          atSign: data.atSign,
        },
      });

      // Signing in after successful registration
      signIn({ userName: data.email, password: data.password });
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  }

  async function checkCustomerExists(data: ICustomerExistsProps) {
    try {
      const response = await api.post("/customer/exists", data);
      return response.data as Record<string, boolean>;
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Não foi possível validar os dados do cadastro.",
        type: "error",
        theme: "dark",
      });
      return null;
    }
  }
  async function getUserProfile() {
    try {
      const response = await api.get("/customer/info");
      const customerData = { ...customer, ...response.data.customer };

      setCustomer({
        customer: {
          ...customerData,
        },
        customerInfo: response.data.customerInfo,
      });
    } catch (error: any) {
      // console.error(
      //   'Error while loading the user profile: ',
      //   error,
      // )
    }
  }

  async function getCustomerBalance() {
    try {
      const response = await api.get("/customer/dashboard/financial-summary");

      setBalance(response.data);
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  }

  async function signOut() {
    setCustomer(undefined);
    localStorage.removeItem(`@${config.appAgent}:customer`);
    openSession();

    clearHash();

    pushToastify({
      message: "Sessão encerrada com sucesso.",
      type: "success",
      theme: "dark",
    });
  }

  async function checkSavedSession() {
    toggleLockLoading(true);
    try {
      const data = localStorage.getItem(`@${config.appAgent}:customer`);
      const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
      if (!sessionId) {
        await openSession();
      } else {
        await getSessionInfo();
      }
      if (data && sessionId) {
        await getUserProfile();
      }
    } catch {
      await openSession();
    }
    toggleLockLoading(false);
  }

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      async (response) => {
        if (response.headers["token-id"]) {
          setSessionId(response.headers["token-id"]);
        }
        return response;
      },
      async (error) => {
        const config = error.config;
        const errorCode = String(error?.response?.data?.errorCode || "");
        if (errorCode === "CSRFX004" || errorCode === "CSRFX005") {
          if (config && config.retryCount < 1) {
            config.retryCount += 1;
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const session = await getSessionInfo();

            config.headers["token-id"] = session?.data.id;
            config.headers["token-csrf"] = session?.data.csrf;

            if (session?.data.id) {
              localStorage.setItem(
                `@${config.appAgent}:token-id`,
                String(session?.data.id),
              );
            }
            if (session?.data.csrf) {
              localStorage.setItem(
                `@${config.appAgent}:token-csrf`,
                String(session?.data.csrf),
              );
            }

            return api(config);
          }
        } else if (errorCode.match("CSRFX")) {
          signOut();
          await openSession();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkSavedSession();
  }, []);

  useEffect(() => {
    if (customer?.customer?.id) {
      getCustomerBalance();
    }
  }, [customer]);

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        createCustomer,
        checkCustomerExists,
        getUserProfile,
        customer,
        getCustomerBalance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);

